import {
    LayoutBuilderTypeBase,
    LayoutBuilderTypeFull,
} from '@shared/types/process';

import {
    isEqualJsonValues,
    parseJsonValueOrRaw,
    stringifyJsonValue,
} from './flowParseValue';

export type LayoutDependentField = NonNullable<
    LayoutBuilderTypeBase['dependentFields']
>[number];

export function normalizeLayoutDependentFields(
    dependentFields: LayoutBuilderTypeBase['dependentFields'],
) {
    return dependentFields ?? [];
}

export function parseLayoutDependentFields(
    dependentFields: LayoutBuilderTypeBase['dependentFields'],
): LayoutDependentField[] {
    return normalizeLayoutDependentFields(dependentFields).map((item) => ({
        ...item,
        dependentFieldValue:
            item.dependentFieldId && item.dependentFieldValue != null
                ? parseJsonValueOrRaw(item.dependentFieldValue)
                : null,
    }));
}

export function stringifyLayoutDependentFields(
    dependentFields: LayoutBuilderTypeBase['dependentFields'],
): LayoutDependentField[] {
    return normalizeLayoutDependentFields(dependentFields).map((item) => ({
        ...item,
        dependentFieldValue:
            item.dependentFieldValue !== null &&
            item.dependentFieldValue !== undefined
                ? stringifyJsonValue(item.dependentFieldValue)
                : null,
    }));
}

export function getDependentFieldNames(
    dependentFields: LayoutBuilderTypeBase['dependentFields'],
) {
    return normalizeLayoutDependentFields(dependentFields)
        .map((item) => item.dependentFieldId)
        .filter(Boolean) as string[];
}

export function isHiddenByDependentFields(
    dependentFields: LayoutBuilderTypeBase['dependentFields'],
    dependentFieldValues: unknown[],
) {
    const dependencies = normalizeLayoutDependentFields(dependentFields).filter(
        (item) => item.dependentFieldId,
    );

    if (dependencies.length === 0) {
        return false;
    }

    return dependencies.every((dependency, index) => {
        const dependentFieldValue = dependentFieldValues[index];

        if (isEmptyDependencyValue(dependentFieldValue)) {
            return true;
        }

        return !isEqualJsonValues(
            dependency.dependentFieldValue,
            stringifyJsonValue(dependentFieldValue),
        );
    });
}

function isEmptyDependencyValue(value: unknown) {
    return value === null || value === undefined || value === '';
}

/** Process field ids under a layout block (Field itself + descendants). */
export function collectProcessFieldIdsFromLayoutBlock(
    block: LayoutBuilderTypeFull,
): string[] {
    const fieldIds: string[] = [];

    if (block.type === 'Field') {
        fieldIds.push(block.options.processFieldId);
    }

    for (const child of block.children ?? []) {
        fieldIds.push(...collectProcessFieldIdsFromLayoutBlock(child));
    }

    return fieldIds;
}

/**
 * Drop fields that live under dependency-hidden layout blocks.
 * Visibility is decided from `values` before mutation so order is stable.
 * Keys are omitted (not sent as null) so Condition treats them as unset.
 */
export function omitHiddenDependentLayoutFieldValues(
    values: Record<string, unknown>,
    blocks: LayoutBuilderTypeFull[],
): Record<string, unknown> {
    const next = { ...values };

    const visit = (nodes: LayoutBuilderTypeFull[]) => {
        for (const block of nodes) {
            const dependencyNames = getDependentFieldNames(
                block.dependentFields,
            );
            const dependencyValues = dependencyNames.map(
                (fieldId) => values[fieldId],
            );

            if (
                isHiddenByDependentFields(
                    block.dependentFields,
                    dependencyValues,
                )
            ) {
                for (const fieldId of collectProcessFieldIdsFromLayoutBlock(
                    block,
                )) {
                    delete next[fieldId];
                }
                continue;
            }

            if (block.children?.length) {
                visit(block.children);
            }
        }
    };

    visit(blocks);
    return next;
}
